/**
 * Extracted from CartPage.jsx — the "Available coupons" bottom-sheet
 * modal. Triggered from the cart's "View available coupons" button;
 * lists every coupon the customer can use right now (cart already
 * has a restaurant_id, so SHOP-scoped coupons for that shop AND
 * TENANT-scoped coupons both appear).
 *
 * The pass-through ``onApply(code)`` invokes the parent's
 * ``applyCouponFromPicker`` which validates server-side via
 * ``POST /coupons/apply?code=…``.
 */
import { BadgePercent, Loader2 } from "lucide-react";
import { inr2 } from "../../../lib/api";

export default function CouponPickerModal({
    busy, available, loading, onApply, onClose,
}) {
    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center"
            data-testid="coupon-picker"
            onClick={onClose}
        >
            <div
                className="bg-card w-full max-w-md md:max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg inline-flex items-center gap-2">
                        <BadgePercent size={18} className="text-primary" /> Available coupons
                    </h3>
                    <button
                        data-testid="coupon-picker-close"
                        onClick={onClose}
                        className="text-sm font-semibold text-muted-foreground"
                    >Close</button>
                </div>
                {loading ? (
                    <div className="text-center py-8 text-muted-foreground inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 size={16} className="animate-spin" /> Loading coupons…
                    </div>
                ) : !available || available.length === 0 ? (
                    <div className="text-center py-8 text-sm text-muted-foreground" data-testid="coupon-picker-empty">
                        No coupons available right now. Check back later!
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        {available.map((c) => {
                            const isShop = (c.scope || "TENANT").toUpperCase() === "SHOP";
                            const dt = (c.discount_type || c.kind || "").toUpperCase();
                            const dv = c.discount_value ?? c.value ?? 0;
                            const isFree = dt === "FREE_DELIVERY";
                            const min = Number(c.min_cart_value || 0);
                            return (
                                <div
                                    key={c.id || c.code}
                                    data-testid={`coupon-card-${c.code}`}
                                    className="bg-background border border-dashed border-primary/40 rounded-2xl p-3.5 flex items-start gap-3"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                        <BadgePercent size={20} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono font-bold text-sm text-primary">{c.code}</span>
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${isShop ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>
                                                {isShop ? "Shop offer" : "All shops"}
                                            </span>
                                        </div>
                                        <p className="text-sm font-semibold mt-0.5 line-clamp-2">
                                            {c.title || (isFree
                                                ? "Free delivery"
                                                : dt === "PERCENT"
                                                    ? `${dv}% off`
                                                    : `Flat ${inr2(dv)} off`)}
                                        </p>
                                        {c.description && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{c.description}</p>}
                                        {min > 0 && (
                                            <p className="text-[11px] text-muted-foreground mt-1">Min cart {inr2(min)}</p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        data-testid={`coupon-apply-${c.code}`}
                                        onClick={() => onApply(c.code)}
                                        disabled={busy}
                                        className="bg-primary text-primary-foreground rounded-xl px-3 py-2 text-xs font-bold self-center disabled:opacity-50"
                                    >Apply</button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
